import {CloseIcon} from '@chakra-ui/icons';
import {
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react';
import {HookUseTypedQuery, TPath, useDebouncedChange} from '@synako/typed-path';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SearchIcon} from '@/components/svg/SearchIcon';
import {FILTER_BUTTON_PROPS} from '@/components/FilterBar/FilterBarStyles';

/**
 * Recherche texte interne du cadencier : écrit `q` dans l’URL (debounce 300 ms).
 * `variant` fourni par FilterBar (une instance desktop + une mobile).
 * Desktop : input inline. Mobile : loupe → input flex (Promo/Filtrer en icônes).
 */
export const CadencierSearchInput: React.FC<{
  filtersHook: HookUseTypedQuery<TPath<{Query: {q?: string}}>>;
  variant: 'desktop' | 'mobile';
  onExpandedChange?: (expanded: boolean) => void;
}> = ({filtersHook, variant, onExpandedChange}) => {
  const {t} = useTranslation();
  const debouncedChange = useDebouncedChange(filtersHook, 'q', 300);
  // `useDebouncedChange` ne relit pas l'URL après l'init. Le brouillon suit
  // la frappe ; on se recale seulement quand `q` change de l'extérieur.
  const urlValue = filtersHook.params.q ?? '';
  const [draft, setDraft] = useState(urlValue);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const hasQuery = draft.length > 0;
  const label = t('recherche.recherche_produits');
  const isMobile = variant === 'mobile';

  useEffect(() => {
    setDraft(urlValue);
    // Annule une frappe encore en debounce, qui réécrirait l'ancienne valeur.
    if (draftRef.current !== urlValue) {
      debouncedChange.change(urlValue || undefined);
    }
    // `debouncedChange` est un nouvel objet à chaque rendu ; `.change` est stable.
    // L'ajouter relancerait l'effet pendant la frappe et écraserait le brouillon.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlValue]);

  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const setExpandedAndNotify = (next: boolean) => {
    setExpanded(next);
    if (isMobile) onExpandedChange?.(next);
  };

  useEffect(() => {
    if (hasQuery && isMobile) {
      setExpanded(true);
      onExpandedChange?.(true);
    }
  }, [hasQuery, isMobile, onExpandedChange]);

  useEffect(() => {
    if (isMobile && expanded) {
      inputRef.current?.focus();
    }
  }, [isMobile, expanded]);

  // Pas de repli au blur : sinon le clic sur Promo / Filtrer est mangé.
  // Repli mobile uniquement via la croix.
  const inputField = (
    <Input
      ref={inputRef}
      h={FILTER_BUTTON_PROPS.height}
      pl='40px'
      pr={hasQuery || (isMobile && expanded) ? '40px' : undefined}
      bg={FILTER_BUTTON_PROPS.bg}
      border='2px'
      borderColor={hasQuery ? 'gray.dark' : 'gray.light'}
      borderRadius={FILTER_BUTTON_PROPS.borderRadius}
      fontSize='xs'
      fontWeight='500'
      color='blue.text'
      aria-label={label}
      placeholder={label}
      _placeholder={{color: 'blue.text', opacity: 0.7}}
      _hover={{borderColor: hasQuery ? 'gray.dark' : 'gray.light'}}
      _focus={{
        borderColor: 'gray.dark',
        boxShadow: 'none',
      }}
      value={draft}
      onChange={(e) => {
        const next = e.target.value;
        setDraft(next);
        debouncedChange.change(next || undefined);
      }}
    />
  );

  const clearButton = (hasQuery || (isMobile && expanded)) && (
    <InputRightElement h={FILTER_BUTTON_PROPS.height}>
      <IconButton
        aria-label={t('cadencier.effacer_recherche')}
        icon={<CloseIcon boxSize={2.5} />}
        variant='ghost'
        size='xs'
        color='gray.dark'
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => {
          setDraft('');
          debouncedChange.change(undefined);
          if (isMobile) setExpandedAndNotify(false);
        }}
      />
    </InputRightElement>
  );

  if (isMobile && !expanded) {
    return (
      <IconButton
        flexShrink={0}
        aria-label={label}
        icon={
          <SearchIcon
            width='16px'
            height='16px'
          />
        }
        h={FILTER_BUTTON_PROPS.height}
        w={FILTER_BUTTON_PROPS.height}
        minW={FILTER_BUTTON_PROPS.height}
        bg={FILTER_BUTTON_PROPS.bg}
        border='2px'
        borderColor={hasQuery ? 'gray.dark' : 'gray.light'}
        borderRadius={FILTER_BUTTON_PROPS.borderRadius}
        onClick={() => {
          setExpandedAndNotify(true);
        }}
      />
    );
  }

  if (isMobile && expanded) {
    return (
      <InputGroup
        flex='1 1 140px'
        minW='120px'
        h={FILTER_BUTTON_PROPS.height}
        transition='width 0.2s ease, flex 0.2s ease, min-width 0.2s ease, opacity 0.15s ease'
      >
        <InputLeftElement
          pointerEvents='none'
          h={FILTER_BUTTON_PROPS.height}
        >
          <SearchIcon
            width='16px'
            height='16px'
          />
        </InputLeftElement>
        {inputField}
        {clearButton}
      </InputGroup>
    );
  }

  return (
    <InputGroup
      flexShrink={0}
      w='180px'
      h={FILTER_BUTTON_PROPS.height}
    >
      <InputLeftElement
        pointerEvents='none'
        h={FILTER_BUTTON_PROPS.height}
      >
        <SearchIcon
          width='16px'
          height='16px'
        />
      </InputLeftElement>
      {inputField}
      {clearButton}
    </InputGroup>
  );
};
